import { produce } from "immer";
import { noop, omit } from "lodash-es";
import { v4 as uuid } from "uuid";
import * as queryString from "qs";
import * as actionTypes from "../actionTypes";
import { CLEAR_BLOCKS } from "../actionTypes";
import {
  defaultPage,
  recordsPerPage,
} from "../../../utils/constants/constants";
import { nestedPullMatched } from "../../../utils/func";
import axiosInstance from "../../../axios";
import queryBuilder from "../queryBuilder/queryBuilder";
import { handleError, reportError } from "../../../utils/errorHandling";
import axios from "axios";
import { hashQueryExecCall } from "../../../utils/crypto";
import { compareByOperator } from "../../../utils/constants/operators";
import { loadHeaderConfiguration, setActiveTab } from "../layout";
import { removeDuplicateOverride } from "../../../Pages/TableEditor/utils/tableEditorHelper";
import { handleAiStreamingResponse } from "../../../utils/aiStreamHandler";
import { HEADER_VISUALIZATION_UUID } from "../../../utils/axios/common";
import { isParameterized } from "../../../utils/queries";
import { showToastWithTimeout } from "../message";

let execAbortController = new AbortController();

function reduceWithConditions(acc, curr, runQueryOnFilters = []) {
  const settings = runQueryOnFilters.find(
    (settings) => settings.name === curr.key
  );

  if (settings?.operator) {
    const { operator, value } = settings;
    if (compareByOperator(curr.value, value, operator)) {
      acc.push(curr);
    }
  } else {
    acc.push(curr);
  }

  return acc;
}

// Function to filter menu filters based on filters to be run
function getRunQueryFilters(menuFilters, configFilters, runQueryOnFilters) {
  const filters = menuFilters.filter((filter) =>
    runQueryOnFilters.map((settings) => settings.name).includes(filter.name)
  );

  // get runQueryOnFilters if they are in query filters array
  const appliedLocalFilters = (configFilters ?? [])
    .filter((filter) =>
      runQueryOnFilters.map((settings) => settings.name).includes(filter.type)
    )
    // map query filters to menuFilters structure
    .map((filter) => ({
      values: {
        value: filter.value,
        checked: true,
        key: filter.type,
        operator: filter.operator,
      },
    }));

  // Retrieve checked values from the filtered filters
  return getCheckedFilterValues(
    [...appliedLocalFilters, ...filters],
    runQueryOnFilters
  );
}

// Function will filter checked values if there is a condition in runQueryOnFilters item
function respectToConditions(selectedFilters, runQueryOnFilters) {
  return selectedFilters.reduce(
    (acc, curr) => reduceWithConditions(acc, curr, runQueryOnFilters),
    []
  );
}

// Function to get checked values from an array of filters
export function getCheckedFilterValues(filters, runQueryOnFilters) {
  const selectedFilters = filters
    .flatMap((filter) => filter.values)
    .filter((val) => val.checked);

  return respectToConditions(selectedFilters, runQueryOnFilters);
}

// Function to execute a query without making an API call
function executeQueryWithoutApiCall(
  dispatch,
  actionProperties,
  forPreview,
  query
) {
  dispatch({ type: actionTypes.RUN_ON_QUERY_FILTERS_CHECKED, checked: true });
  dispatch({
    type: actionTypes.EXECUTE_QUERY_SUCCESS,
    ...{
      ...actionProperties,
      meta: {
        forPreview,
        ...(forPreview && { chart: query }),
      },
      results: { data: [] },
    },
  });
}

export function executeQuery(
  query,
  remainingCharts,
  nextFunction = loadCharts,
  slice = "dashboard",
  loadBundleId,
  forPreview,
  forHeaderBlocks,
  page
) {
  return async (dispatch, getState) => {
    const editorLimit = forPreview && !query.limit ? 1000 : query.limit;

    const qs = queryBuilder(
      { ...query, limit: editorLimit },
      getState,
      false,
      "chart",
      { forHeaderBlocks },
      page
    );

    const {
      convertGetToPost,
      inComparisonMode,
      runQueryOnFilters,
      filters,
      queryId,
      visualizationId,
    } = query;

    const { dateFilters, comparisonMode, menuFilters } = getState().layout;
    const { checked, count } = comparisonMode ?? {};

    if (inComparisonMode && checked !== count && Number.isFinite(count)) {
      return;
    }

    // Create a new controller and add it to the list
    const controller = (execAbortController = new AbortController());

    const api = {
      public: false,
      method: convertGetToPost ? "POST" : "GET",
      endpoint: `api/v1/queries/${queryId}/exec?${convertGetToPost ? "" : qs}`,
      signal: controller.signal,
      headers: {
        [HEADER_VISUALIZATION_UUID]: visualizationId,
      },
      reportAllErrors: true,
      ...(convertGetToPost && {
        payload: queryString.parse(qs, { depth: 10 }), // for the nested array parsing we need set depth as option
      }),
    };

    let cacheKey;
    try {
      cacheKey = loadBundleId && (await hashQueryExecCall(api, loadBundleId));
    } catch (e) {
      reportError(e);
      // Fall back to not using the cache.
    }

    const actionProps = {
      cacheKey,
      loadBundleId,
      dateFilters,
      meta: {
        api,
        forPreview,
        ...(forPreview && { chart: query }),
      },
      slice,
      visualizationId,
      chart: query,
    };

    // Check if 'runQueryOnFilters' is provided
    if (runQueryOnFilters) {
      // Get the checked filters from 'menuFilters' based on 'runQueryOnFilters'
      const checked = getRunQueryFilters(
        menuFilters,
        filters,
        runQueryOnFilters
      );

      // If there are checked filters, run the query; otherwise, execute query without making an API call
      checked.length
        ? await runQuery(true)
        : executeQueryWithoutApiCall(dispatch, actionProps, forPreview, query);
    } else {
      // If 'runQueryOnFilters' is not provided, simply run the query
      await runQuery();
    }

    async function runQuery(dropRunQueryOnFilters) {
      if (dropRunQueryOnFilters) {
        dispatch({
          type: actionTypes.RUN_ON_QUERY_FILTERS_CHECKED,
          checked: false,
        });
      }

      // Look in previously executed results to see if this query was already
      // executed to just take its data results without having call the API.
      const state = getState();
      const blocks = cacheKey
        ? [
            ...(state.layout.header?.blocks || []),
            ...(state.dashboard?.blocks || []),
          ]
        : [];

      const cachedResult = blocks
        .flatMap((block) => block.charts)
        .find(
          (chart) =>
            !chart.loading && !chart.refreshing && chart.cacheKey === cacheKey
        );

      if (cachedResult) {
        // Use the cache.
        dispatch({
          type: actionTypes.EXECUTE_QUERY_SUCCESS,
          ...actionProps,
          meta: { ...actionProps.meta, api: null },
          results: {
            data: cachedResult.data,
            meta: cachedResult.meta,
          },
          cacheUsed: true,
        });
      } else {
        await dispatch({
          type: actionTypes.EXECUTE_QUERY_START,
          ...actionProps,
        });
      }
    }

    if (remainingCharts?.length) {
      const aborted = controller.signal.aborted;

      const param = forHeaderBlocks
        ? getAllHeadersOnAborted(remainingCharts, aborted)
        : [remainingCharts, loadBundleId];

      if (!aborted) {
        dispatch(nextFunction(param));
      }
    }
  };
}

function getAllHeadersOnAborted(remainingCharts, aborted) {
  const [
    loadBundleId,
    remaining,
    dashboardName,
    visualizationId,
    filterSelect,
    resetFilters,
    allHeaders,
  ] = remainingCharts;

  // if header request aborted start from the first one
  return [
    loadBundleId,
    aborted ? allHeaders : remaining,
    dashboardName,
    visualizationId,
    filterSelect,
    resetFilters,
    allHeaders,
  ];
}

const checkForIgnoreCondition = (filters, ignoreMenuFilters) => {
  if (!filters) {
    return [];
  }

  return [filters]
    .flat()
    .filter((filter) => !ignoreMenuFilters.includes(filter.type));
};

const removeDuplicates = (filters) => {
  return filters.filter(
    (filter, index, self) =>
      index ===
      self.findIndex(
        (selfFilter) =>
          selfFilter.type === filter.type && selfFilter.value === filter.value
      )
  );
};

export const loadRowExpandChart =
  (filters, query, exportAll) => (dispatch, getState) => {
    if (query.localRowExpandGrouping) {
      return;
    }

    const nonIgnoreFilters = checkForIgnoreCondition(
      filters,
      query.ignoreMenuFilters || []
    );

    const globalFilters = (query.filters || []).filter(
      // We use this parameter in the Power Editor to eliminate filters
      // that users have clicked on within the drilldown hierarchy.
      // This parameter will be automatically removed during the Power Editor validation on save.
      // However, it is necessary for us to manually remove it from the filters array when combining drilldown hierarchy and row expand functionality.
      (filter) => !filter.removeOnSave
    );

    const realFilters = removeDuplicates([
      ...globalFilters,
      ...nonIgnoreFilters,
    ]);

    const qs = queryBuilder({ ...query, filters: realFilters }, getState);
    const { convertGetToPost } = query;
    const dateFilters = getState().layout.dateFilters;

    const visualizationUuid = query.uuid;
    const isUsage = query.usage;
    const apiPrimary = {
      public: false,
      method: convertGetToPost ? "POST" : "GET",
      endpoint: `api/v1/queries/${
        isUsage ? "usage-analytics" : query["queryId"]
      }/exec?${convertGetToPost ? "" : qs}`,
      headers: {
        [HEADER_VISUALIZATION_UUID]: visualizationUuid,
      },
      reportAllErrors: true,
      ...(convertGetToPost && { payload: queryString.parse(qs) }),
    };

    const { loaded, data } =
      getState().dashboard.rowExpandedCharts[query.visualizationId] || {};

    dispatch({
      type: exportAll
        ? actionTypes.ROW_EXPAND_EXEC_QUERY_EXPORT_ALL_START
        : actionTypes.ROW_EXPAND_EXEC_QUERY_START,
      dateFilters: dateFilters,
      meta: {
        api: apiPrimary,
      },
      filters: realFilters,
      visualizationId: query.visualizationId,
      chart: { ...query, loaded, data: data || [] },
    });
  };

export const clearExportAllObject = () => (dispatch) => {
  dispatch({ type: actionTypes.CLEAR_EXPORT_ALL_OBJECT });
};

// if key exist then clear that row, if not then clear all
export const clearExpandedRows = (key) => (dispatch) => {
  dispatch({ type: actionTypes.CLEAR_ALL_ROW_EXPANDED_CHARTS, key });
};

export const initDashBoardLoader =
  (name, visualizationId) => (dispatch, getState) => {
    const loadedDashboard = getState().dashboard.name;
    const loadedHeader = getState().layout.header?.blocks || [];
    const blocks = getState().dashboard.blocks;
    const { filtersUpdateHeaderKPIs } = getState().layout;

    const allHeaderLoaded =
      loadedHeader.length > 0
        ? loadedHeader
            .map((block) => block.charts)
            .flat()
            .filter((chart) => chart.id && chart.type !== "Vertical Divider")
            .every((chart) => chart.loaded || chart.error || !chart.refreshing)
        : true;

    if (
      !allHeaderLoaded ||
      (!loadedHeader.length && !loadedDashboard) ||
      filtersUpdateHeaderKPIs
    ) {
      return dispatch(loadHeaderConfiguration([name, visualizationId]));
    }

    const tabHasChanged = loadedDashboard !== name;
    if (tabHasChanged && allHeaderLoaded) {
      return dispatch(loadDashboardConfiguration(name));
    }

    const anyNotLoaded = blocks.find((b) => b.charts.find((c) => !c.loaded));

    if (anyNotLoaded) {
      return dispatch(initLoadCharts(name));
    }
  };

export function loadDashboardConfiguration(
  name,
  visualizationId,
  configOnly,
  loadBundleId
) {
  return (dispatch, getState) => {
    const { layout } = getState();
    const page = activePageFromTabs();

    function activePageFromTabs() {
      return (
        layout.tabs.find((page) => page.slug === name) ||
        layout.tabs.find((page) => page) ||
        defaultPage
      );
    }

    const ax = axiosInstance;

    return performLoadDashboardConfiguration(
      page,
      layout.domain,
      layout.term,
      visualizationId,
      ax,
      dispatch,
      configOnly,
      loadBundleId
    ).then(() => {
      // do nothing
    });
  };
}

let loadDashboardConfigurationAbortController = new AbortController();

export async function performLoadDashboardConfiguration(
  page,
  domain,
  term,
  visualizationId,
  ax,
  dispatch,
  configOnly,
  loadBundleId
) {
  // Passing a visualization Id indicates we are on a detail screen with only a single vis
  const afterAction = visualizationId ? loadDetail : setChartVisibility;

  const endpoint = `api/v1/pages/${page.uuid}`;

  const type = actionTypes.LOAD_DASHBOARD_CONFIGURATION_SUCCESS;

  // Abort any previous request.
  loadDashboardConfigurationAbortController.abort();
  loadDashboardConfigurationAbortController = new AbortController();

  return ax
    .get(endpoint, {
      responseType: "json",
      signal: loadDashboardConfigurationAbortController.signal,
    })
    .then((res) => {
      if (!res.data) {
        // Not-found JSON configs requested from this FE project's public
        // directory may return 200 OK with HTML. Consider those not found.
        throw new Error(
          "The JSON configuration for the page could not be found."
        );
      }

      try {
        const results = normalizeResponse(res.data);

        const result = dispatch({
          type,
          term,
          results,
        });

        dispatch(setActiveTab(res.data.data));

        if (!configOnly) {
          if (afterAction === loadDetail) {
            dispatch(loadDetail(visualizationId));
          } else if (afterAction === setChartVisibility) {
            dispatch(setChartVisibility(loadBundleId));
          } else {
            dispatch(afterAction());
          }
        }

        return result;
      } catch (e) {
        // This error only writes to log now, is not visible to user
        handleError(e);
      }
    })
    .catch((err) => {
      handleError(err);
      if (axios.isCancel(err)) {
        return;
      }
      dispatch({
        type: actionTypes.LOAD_DASHBOARD_CONFIGURATION_FAIL,
        message: "Configuration not available",
      });
    });
}

export function normalizeResponse(response, notHide) {
  if (!response) return response;
  if (!response.data.blocks)
    throw new Error(
      "Something went wrong. Blocks must be defined. Please reload."
    );

  return {
    ...response.data,
    blocks: response.data.blocks
      // api using hide not hidden property, response come from api its not come from reducer where we already changed this hide to hidden
      .filter((block) => notHide || !block.hide)
      .map((block) => {
        const { settings } =
          block.visualizations.find((v) => Array.isArray(v.settings)) ?? {};

        return {
          ...block,
          segmentTitle: block.styles?.segmentTitle ?? "",
          charts: settings ?? block.visualizations.map(normalizeVisualization),
          visualizations: undefined,
        };
      }),
  };
}

function normalizeVisualization(vis) {
  const flatVis = { ...vis, ...vis.settings };
  return produce(flatVis, (draftVis) => {
    draftVis.queryId = draftVis.query?.uuid ?? draftVis.queryId;
    draftVis.visualizationId = draftVis.uuid;
    delete draftVis.query;
    delete draftVis.settings;
  });
}

export const loadDetail = (visualizationId) => (dispatch, getState) => {
  const query = getState().dashboard.blocks.reduce((acc, curr) => {
    return (
      curr.charts.find((c) => c.visualizationId === visualizationId) || acc
    );
  }, {});

  if (query.visualizationId) {
    dispatch(executeQuery(query));
  }
};

export const setChartVisibility =
  (loadBundleId, visualizationId) => (dispatch, getState) => {
    const { menuFilters } = getState().layout;
    const ret = dispatch({
      type: actionTypes.SET_CHART_VISIBILITY,
      filters: menuFilters.map((item) => item.values).flat(),
    });

    dispatch(initLoadCharts(visualizationId, loadBundleId));

    return ret;
  };

export function initLoadCharts(
  visualizationUuid,
  loadBundleId = uuid(),
  chart // comments chart override
) {
  return (dispatch, getState) => {
    const charts = chart
      ? [chart]
      : justCharts(getState)
          .filter((chart) => {
            if (Array.isArray(visualizationUuid)) {
              return visualizationUuid.includes(chart.uuid);
            }

            return visualizationUuid ? chart.uuid === visualizationUuid : true;
          })
          .filter((chart) => chart.type !== "Vertical Divider");

    dispatch({
      type: actionTypes.SET_LOAD_BUNDLE,
      loadBundleId,
      charts,
      visualizationUuid,
    });
    dispatch(loadCharts([charts, loadBundleId]));
  };
}

export const loadCharts = (args) => (dispatch, getState) => {
  const [remainingCharts, loadBundleId] = args;

  // if loadBundleId doesn't match, stop
  // Short circuit execution id dashboard has changed
  const activeLoadBundleId = getState().dashboard.loadBundleId;
  if (activeLoadBundleId !== loadBundleId) return null;

  if (!remainingCharts.length) {
    return;
  }

  const [current, ...remaining] = remainingCharts;
  dispatch(
    executeQuery(current, remaining, loadCharts, "dashboard", loadBundleId)
  );
};

export const justCharts = (getState) => {
  const dashboard = getState().dashboard;
  const term = getState().layout.term;
  const menuFilters = getState().layout.menuFilters || [];
  const activeMenuFilters = menuFilters
    .filter((mf) => mf.values.find((v) => v.checked))
    .map((mf) => mf.values[0].key);

  return (dashboard?.blocks ?? [])
    .filter((b) => !b.section)
    .reduce((acc, curr) => [...acc, ...curr.charts], [])
    .filter((c) => c.visualizationId)
    .filter(
      (c) => !c.availableForTerms || c.availableForTerms.find((d) => d === term)
    )
    .filter((c) => c.type !== "ActiveTable")
    .filter(
      (c) =>
        !c.hideOnFilters ||
        !c.hideOnFilters.find((f) => activeMenuFilters.find((m) => m === f))
    );
};

// Detail charts
export const initDetails = (id) => (dispatch, getState) => {
  const matchedChart = nestedPullMatched(
    getState().dashboard.blocks,
    "charts",
    "visualizationId",
    id
  );
  if (!matchedChart) {
    loadDetail(id);
  }
};

function cloneQuery(getState, id) {
  const query = getState()
    .dashboard.blocks.map((block) => block.charts)
    .map((chart) => chart.find((item) => item.visualizationId === id))
    .filter((currentItem) => currentItem)[0];

  return omit(query, ["includeFullWeek", "limit", "orders", "overrides"]);
}

export const loadAllTableRecords =
  (id, showAll, page, dropPreviousData) => async (dispatch, getState) => {
    const copyQuery = cloneQuery(getState, id);

    if (!showAll) {
      dispatch({ type: actionTypes.CLEAR_ALL_TABLE_RECORDS });
    } else {
      if (copyQuery.alternateAllRecordsDateKey) {
        copyQuery.dateKey = copyQuery.alternateAllRecordsDateKey;
      }

      if (copyQuery.alternateAllRecordsQuery) {
        copyQuery.queryId = copyQuery.alternateAllRecordsQuery;
        copyQuery.isParameterized = getIsParameterized(
          copyQuery.alternateAllRecordsQuery,
          getState
        );
      }

      const qs = queryBuilder(copyQuery, getState, true);

      const queryId = copyQuery.queryId;

      dispatch({
        type: actionTypes.SHOW_ALL_TABLE_RECORDS_START,
        meta: {
          api: {
            method: "GET",
            endpoint: `api/v1/queries/${queryId}/exec?${qs}&perPage=${recordsPerPage}&page=${page}`,
            reportAllErrors: true,
            toastOnFailure: true,
          },
        },
        dropPreviousData,
      });
    }
  };

function getIsParameterized(alternateAllRecordsQuery, getState) {
  const queries = getState().dataSettings?.tinyQueries ?? [];
  const query = queries.find(
    (query) => query.uuid === alternateAllRecordsQuery
  );

  return isParameterized(query);
}

export const downloadTable =
  (format, id, showAll, headers = []) =>
  (dispatch, getState) => {
    const query = matchQueryFromStore();
    const { convertGetToPost } = query;

    const useAlternateQuery = query.alternateAllRecordsQuery && showAll;

    if (useAlternateQuery) {
      query.queryId = query.alternateAllRecordsQuery;

      // check alternate query type
      query.isParameterized = getIsParameterized(
        query.alternateAllRecordsQuery,
        getState
      );
    }

    const showAllQuery = getShowAllQuery();

    const qs = queryBuilder(
      showAll ? omit(showAllQuery, ["limit"]) : omit(query, ["limit"]),
      getState,
      !!showAll,
      format
    );

    const endpoint = generateEndpoint();

    dispatch({
      type: actionTypes.DOWNLOAD_TABLE_DATA_START,
      meta: {
        api: {
          method: convertGetToPost ? "POST" : "GET",
          endpoint,
          headers: {
            [HEADER_VISUALIZATION_UUID]: id,
          },
          file: true,
          format,
          reportAllErrors: true,
          fileName: generateName(),
          ...(convertGetToPost && {
            payload: queryString.parse(qs),
          }),
          toastOnFailure: true,
        },
      },
    });

    function matchQueryFromStore() {
      return getState()
        .dashboard.blocks.map((block) => block.charts)
        .map((chart) => chart.find((item) => item.visualizationId === id))
        .filter(Boolean)
        .map((chart) => ({
          ...chart,
          overrides: chart.overrides?.filter(
            (override) => !showAll && !override.mapping?.hideOnTables
          ),
        }))[0];
    }

    function generateName() {
      const { title, segmentTitle } = getState().dashboard.blocks.find(
        (item) => item.charts.find((c) => c.visualizationId === id) && item
      );
      return title || segmentTitle || "InsightOut Data";
    }

    function getShowAllQuery() {
      const overrides = headers.map((h) => ({ name: h }));

      return {
        ...query,
        ...(query.alternateAllRecordsDateKey && {
          dateKey: query.alternateAllRecordsDateKey,
        }),
        overrides,
        dataMerge: false,
      };
    }

    function generateEndpoint() {
      const hasOverrideQuery = showAll ? query.alternateAllRecordsQuery : false;
      const queryId = hasOverrideQuery || showAllQuery["queryId"];
      return `api/v1/queries/${queryId}/exec?${convertGetToPost ? "" : qs}`;
    }
  };

export const updateValues = (values) => () => {
  axiosInstance
    .post(`api/v1/queries/update-values`, { entries: values })
    .then(noop)
    .catch((err) => {
      console.error(err);
    });
};

export const cancelCalls = () => {
  return (dispatch) => {
    dispatch({ type: actionTypes.STOP_DASHBOARD_SYNC });
    loadDashboardConfigurationAbortController.abort();
  };
};

export const updateTablePagination =
  (chart, page, forPreview) => (dispatch) => {
    dispatch({ type: actionTypes.HANDLE_TABLE_PAGINATION, chart });
    dispatch(
      executeQuery(
        { ...chart, page, ignoreAbortSignal: true },
        null,
        null,
        "dashboard",
        null,
        forPreview
      )
    );
  };

export const removeExpandRowsOnExport = (params) => (dispatch) => {
  dispatch({
    type: actionTypes.REMOVE_DEEPER_EXPANDED_ROWS,
    keys: params.ids,
    ...params,
  });
};

export function clearBlocks() {
  return (dispatch) => {
    dispatch({ type: CLEAR_BLOCKS });
    // Abort any query execs.
    execAbortController.abort();
  };
}

export function clearPowerEditorPreview() {
  return (dispatch) => {
    dispatch({ type: actionTypes.REFRESH_POWER_EDITOR_PREVIEW });
  };
}

const replaceDownloadSupport =
  ({ queryId, visualizationId, endpoint }, format, qs, fileName) =>
  async (dispatch) => {
    try {
      dispatch({ type: actionTypes.DOWNLOAD_TABLE_DATA_START });

      const res = await axiosInstance({
        method: "POST",
        url: endpoint ?? `api/v1/queries/${queryId}/exec?`,
        data: {
          ...queryString.parse(qs),
          file: true,
          format,
          reportAllErrors: true,
          fileName,
        },
      });

      const message =
        "This type of export can be used with amount of results less then 1000";

      async function download(format) {
        await dispatch({
          type: actionTypes.DOWNLOAD_TABLE_DATA_START,
          meta: {
            api: {
              method: "POST",
              endpoint: endpoint ?? `api/v1/queries/${queryId}/exec`,
              headers: {
                [HEADER_VISUALIZATION_UUID]: visualizationId,
              },
              file: true,
              reportAllErrors: true,
              toastOnFailure: true,
              fileName,
              payload: { ...queryString.parse(qs), format },
              format,
            },
          },
        });
      }

      if (res.data?.data?.message === message) {
        showToastWithTimeout(
          `This export type supports fewer than 1,000 results. Your export has been converted to a standard export without images.`,
          "warning"
        );
        await download("excel");
      } else {
        await download(format);
      }
    } catch (err) {
      handleError(err);
      dispatch({ type: actionTypes.DOWNLOAD_TABLE_DATA_FAIL });
    }
  };

export function resetDownload() {
  return {
    type: actionTypes.DOWNLOAD_TABLE_DATA_RESET,
  };
}

export const apiExportAllDrilldownSubtitles =
  (
    chart,
    title,
    setExporting,
    format = "excel",
    overrides,
    overridesOtherExcelExport
  ) =>
  async (dispatch, getState) => {
    setExporting(true);
    // empty array for recursive accumulator
    const allOverrides = overrides ?? [];

    function getOverridesRecursively(chart) {
      // Add subtitles if they exist
      if (chart.overrides) {
        allOverrides.push(...chart.overrides);
      }

      // Handle rowExpandVisualizationParams recursively
      const params = chart.rowExpandVisualizationParams;
      if (params) {
        if (chart.dynamicDrilldowns) {
          Object.values(params).forEach(getOverridesRecursively);
        } else {
          getOverridesRecursively(params);
        }
      }
    }

    if (!overrides) {
      getOverridesRecursively(chart);
    }

    const qs = queryBuilder(
      omit(
        {
          ...chart,
          overrides: removeDuplicateOverride(allOverrides),
          overridesOtherExcelExport,
        },
        "limit"
      ),
      getState,
      null,
      format
    );

    const { queryId, convertGetToPost, visualizationId } = chart;

    await dispatch(
      replaceDownloadSupport(
        { queryId, convertGetToPost, visualizationId },
        format,
        qs,
        title
      )
    );

    setExporting(false);
  };

export function handleSnackbarErrors(error) {
  if (!error || !error.data) {
    return;
  }

  return Object.values(error.data).map((error) => error[0]);
}

export const setRowExpandedChartsHeight = (index, height) => (dispatch) => {
  dispatch({ type: actionTypes.SET_ROW_EXPANDED_CHARTS_HEIGHT, index, height });
};

export const seveExpandedRowHiddenColumns =
  (visualizationId, keys, arrays) => (dispatch) => {
    dispatch({
      type: actionTypes.SAVE_EXPANDED_ROW_HIDDEN_COLUMNS,
      visualizationId,
      keys,
      arrays,
    });
  };

export const aiCloseSidebar = () => (dispatch) => {
  dispatch({ type: actionTypes.AI_CLOSE_SIDEBAR });
};

export const aiResetAll = () => (dispatch) => {
  dispatch({ type: actionTypes.AI_CLOSE_SIDEBAR });
  dispatch({ type: actionTypes.AI_CLEAR_TOOL_RESPONSE });
};

export const aiSetResultsModalOpen = (open) => ({
  type: actionTypes.AI_RESULTS_MODAL_OPEN,
  open,
});

export const aiSetActiveResultMessageId = (messageId) => ({
  type: actionTypes.AI_SET_ACTIVE_RESULT_MESSAGE_ID,
  messageId,
});

export const aiToggleSideBar = (state) => (dispatch) => {
  dispatch({ type: actionTypes.AI_TOGGLE_SIDEBAR, state });
};

export function aiAnalyzeVisualizationStreamed(
  title,
  visualizationConfig,
  options
) {
  options ??= {};
  const { systemPromptOverride } = options;
  return async (dispatch) => {
    dispatch({
      type: actionTypes.AI_ANALYZE_VISUALIZATION_START,
    });

    let description = "";
    let conversationId = null;
    const visualizationId = visualizationConfig?.visualizationId;

    await handleAiStreamingResponse({
      endpoint: "/analyze_visualization_streamed",
      body: {
        title,
        visualization_config: visualizationConfig,
        system_prompt_override: systemPromptOverride,
      },
      onProgress: (parsedData) => {
        if (parsedData.type === "content") {
          description += parsedData.data;
          dispatch({
            type: actionTypes.AI_ANALYZE_VISUALIZATION_PROGRESS,
            description,
          });
        } else if (parsedData.type === "conversation_id") {
          conversationId = parsedData.data;
        }
      },
      onError: (error) => {
        dispatch({
          type: actionTypes.AI_ANALYZE_VISUALIZATION_FAIL,
          error,
        });
      },
      onComplete: () => {
        dispatch({
          type: actionTypes.AI_ANALYZE_VISUALIZATION_SUCCESS,
          description,
          title,
          visualizationId,
          conversationId,
        });
      },
    });
  };
}

export function aiSendFollowUpQuestionStreamed(question, signal, options) {
  options ??= {};
  const { systemPromptOverride } = options;
  return (dispatch, getState) => {
    const { conversationId, visualizationId, title } =
      getState().dashboard.aiSidebar;

    if (!conversationId) {
      dispatch({
        type: actionTypes.AI_FOLLOW_UP_FAIL,
        error: "Conversation ID is missing. Please try again.",
      });
      return;
    }

    // Look up the chart from blocks using visualizationId
    let visualizationConfig = null;
    if (visualizationId) {
      const { blocks } = getState().dashboard;
      for (const block of blocks) {
        const chart = block.charts?.find(
          (c) => c.visualizationId === visualizationId
        );
        if (chart) {
          visualizationConfig = chart;
          break;
        }
      }
    }

    dispatch(
      aiSendFollowUpQuestionCommon(
        "/visualization_follow_up_streamed",
        {
          question,
          conversation_id: conversationId,
          system_prompt_override: systemPromptOverride,
          ...(title && { title }),
          ...(visualizationConfig && {
            visualization_config: visualizationConfig,
          }),
        },
        question,
        signal
      )
    );
  };
}

export function aiSendFollowUpQuestionForPowerEditorStreamed(
  chart,
  question,
  signal,
  callback,
  isTtgUser
) {
  return (dispatch, getState) => {
    const { conversationId } = getState().dashboard.aiSidebar;

    const endpoint = conversationId
      ? `/power-editor/conversation/${conversationId}`
      : "/power-editor/start-conversation";

    dispatch(
      aiSendFollowUpQuestionCommon(
        endpoint,
        chart,
        question,
        signal,
        callback,
        isTtgUser
      )
    );
  };
}

export function resetAiError() {
  return (dispatch) => {
    dispatch({ type: actionTypes.AI_RESET_ERROR });
  };
}

export function submitAiMessageFeedback(conversationId, messageId, feedback) {
  return async (dispatch) => {
    dispatch({
      type: actionTypes.AI_MESSAGE_FEEDBACK_SUBMIT,
      messageId,
      feedback,
    });

    try {
      const { submitMessageFeedback } = await import("../../../aiApi");
      await submitMessageFeedback(conversationId, messageId, feedback);

      dispatch({
        type: actionTypes.AI_MESSAGE_FEEDBACK_SUCCESS,
        messageId,
        feedback,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.AI_MESSAGE_FEEDBACK_FAIL,
        messageId,
        error: error.message,
      });
      throw error;
    }
  };
}

export function aiSendFollowUpQuestionForDataExplorerStreamed(
  question,
  signal,
  options
) {
  options ??= {};
  const { systemPromptOverride } = options;
  return (dispatch, getState) => {
    const { conversationId } = getState().dashboard.aiSidebar;
    const { dateFilterType, dateFilters } = getState().layout;
    const endpoint = conversationId
      ? `/data-explorer/conversation/${conversationId}`
      : "/data-explorer/start-conversation";

    const body = {
      question,
      system_prompt_override: systemPromptOverride,
    };

    // Pass the dashboard's active date filter so the AI agent
    // uses it as the default timeframe instead of always MTD
    if (dateFilterType) {
      body.dashboard_date_filter = {
        to_date_type: dateFilterType,
        custom_start: dateFilters?.start?.value || null,
        custom_end: dateFilters?.end?.value || null,
      };
    }

    dispatch(aiSendFollowUpQuestionCommon(endpoint, body, question, signal));
  };
}

function aiSendFollowUpQuestionCommon(
  endpoint,
  body,
  question,
  signal,
  callback,
  isTtgUser
) {
  return async (dispatch) => {
    dispatch({
      type: actionTypes.AI_FOLLOW_UP_START,
      question,
    });

    let messageId = null;

    await handleAiStreamingResponse({
      endpoint,
      body,
      signal,
      onProgress: (parsedData) => {
        if (parsedData.type === "message_id") {
          messageId = parsedData.data;
          // Create an empty message to show loading indicator
          dispatch({
            type: actionTypes.AI_FOLLOW_UP_PROGRESS,
            answer: null,
            messageId,
            eventType: "message_created",
          });
          return;
        }

        // setChartState in power editor
        if (parsedData.type === "visualization_config" && callback) {
          const updatedConfig = JSON.parse(parsedData.data);
          callback(updatedConfig);
          return;
        }

        // Pass the event type (content, thinking, tool_use, tool) to the reducer
        dispatch({
          type: actionTypes.AI_FOLLOW_UP_PROGRESS,
          answer: parsedData.data,
          messageId,
          eventType: parsedData.type,
        });
      },
      onError: (error) => {
        dispatch({
          type: actionTypes.AI_FOLLOW_UP_FAIL,
          error,
        });
      },
      onComplete: () => {
        // Ensure loading state is cleared when streaming completes
        dispatch({
          type: actionTypes.AI_FOLLOW_UP_COMPLETE,
        });
      },
      isTtgUser,
    });
  };
}

export const setDynamicSubTitles =
  (columnKeys, visualizationId) => (dispatch) => {
    dispatch({
      type: actionTypes.SET_DYNAMIC_SUBTITLES,
      columnKeys,
      visualizationId,
    });
  };
